import { withoutCurated, type BulkObservation } from '../lib/latestObsParse';
import { NATIONAL_BUOYS } from '../nationalBuoys';
import { STATIONS } from '../stations';

/**
 * The NDBC stations outside the curated Bay set.
 *
 * Fetched once, lazily, after the Bay has already painted — the whole point is
 * that adding the country costs the home view nothing. It is one request for
 * ~650 stations, against one request per station for CO-OPS, which is why this
 * is affordable at all.
 *
 * The readings are an hourly rollup: median 80 minutes old against 10 for a
 * CO-OPS station fetched directly. They render through the map's ageing state
 * rather than pretending to be current.
 */

export interface NationalStation {
  id: string;
  name: string;
  /** buoy | fixed | oilrig | other */
  type: string;
  lat: number;
  lon: number;
  t: Date;
  speedKt: number | null;
  dirDeg: number | null;
  gustKt: number | null;
  waveHeightM: number | null;
  airTempC: number | null;
  waterTempC: number | null;
}

interface WireRow extends Omit<BulkObservation, 't'> {
  t: string;
}

let loaded: NationalStation[] | null = null;
let inFlight: Promise<NationalStation[]> | null = null;

/**
 * Loads the national set once per session.
 *
 * Concurrent callers share one request — the map fires this on several early
 * move events, and four parallel fetches of the same 100 KB would be a waste
 * paid by the reader.
 */
export function nationalStations(force = false): Promise<NationalStation[]> {
  // `force` re-reads the bulk file on the refresh poll. Without it these were
  // fetched once per page load and never again, so a view too crowded for the
  // per-station refresh kept its opening readings for as long as the tab was
  // open — every marker slowly ageing into amber with nothing to correct it.
  if (loaded && !force) return Promise.resolve(loaded);
  if (inFlight) return inFlight;

  inFlight = fetch('/api/ndbc/latest')
    .then(async (res) => {
      if (!res.ok) throw new Error(`national buoys HTTP ${res.status}`);
      const body = (await res.json()) as { stations?: WireRow[] };
      if (!Array.isArray(body.stations)) throw new Error('no stations in response');

      // Drop anything sitting on a curated station. NDBC republishes most of
      // CO-OPS under C-MAN ids — every Bay station has a twin at 0.0 km — and
      // two markers per station disagreeing by an hour is worse than one.
      const rows = withoutCurated(body.stations, STATIONS);

      loaded = rows.flatMap((r) => {
        const meta = NATIONAL_BUOYS[r.id];
        if (!meta) return [];
        return [{
          id: r.id,
          name: meta.n,
          type: meta.t,
          lat: r.lat,
          lon: r.lon,
          t: new Date(r.t),
          speedKt: r.speedKt,
          dirDeg: r.dirDeg,
          gustKt: r.gustKt,
          waveHeightM: r.waveHeightM,
          airTempC: r.airTempC,
          waterTempC: r.waterTempC,
        }];
      });
      return loaded;
    })
    .catch((err) => {
      // Leave `loaded` null so a later move can retry, but stop the failed
      // promise from being handed to every subsequent caller.
      inFlight = null;
      throw err;
    });

  return inFlight;
}

/** Stations inside the given bounds. */
export function withinBounds(
  stations: readonly NationalStation[],
  bounds: { north: number; south: number; east: number; west: number },
): NationalStation[] {
  return stations.filter(
    (s) =>
      s.lat <= bounds.north && s.lat >= bounds.south &&
      s.lon <= bounds.east && s.lon >= bounds.west,
  );
}
